import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import Glyphicon from '../Glyphicon/index.js';
import Heading from '../Heading/index.js';
import SubHeading from '../SubHeading/index.js';
const supportedUiContexts = ['level-1', 'level-2', 'level-3', 'section-1', 'section-2', 'section-3', 'section-4', 'section-5', 'section-6', 'section-7'];
const deprecatedUiContexts = {
  primary: 'section-1',
  secondary: 'section-2',
  tertiary: 'section-3',
  quaternary: 'section-4'
};
const headingSizes = {
  'level-1': 1,
  'level-2': 2,
  'level-3': 3,
  'section-1': 700,
  'section-2': 600,
  'section-3': 500,
  'section-4': 400,
  'section-5': 300,
  'section-6': 200,
  'section-7': 200
};
const subheadingSizes = {
  'level-1': 1,
  'level-2': 2,
  'level-3': 3,
  'section-1': 600,
  'section-2': 500,
  'section-3': 400,
  'section-4': 300,
  'section-5': 200,
  'section-6': 100,
  'section-7': 100
};
const convertDeprecatedUiContext = context => deprecatedUiContexts[context] || 1;
const getIcon = _ref => {
  let {
    className,
    type,
    ...rest
  } = _ref;
  const glyphiconClassName = clsx('margin-right-2', className);
  return /*#__PURE__*/React.createElement(Glyphicon, Object.assign({
    key: "sectionHeadingIcon",
    className: glyphiconClassName,
    glyph: type,
    "aria-hidden": "true"
  }, rest));
};

/**
 *  Section containerizes an area of content.  It allows for an optional header and subheader string to be applied along with rendering any child content.
 *  The appearance of the Section component can be adjusted via the uiContext prop.
 */
function Section(_ref2) {
  let {
    alignment = 'center',
    uiContext = 'section-1',
    heading,
    subheading,
    headingComponentClass = 'div',
    children,
    headingClassName,
    sectionBodyClassName,
    headingIcon,
    className,
    headingProps = {},
    ...rest
  } = _ref2;
  // Add backwards-compatible support for latinate uiContext naming
  if (!supportedUiContexts.includes(uiContext)) {
    uiContext = convertDeprecatedUiContext(uiContext);
  }
  const alignmentClassName = `text-${alignment}`;
  const getHeading = () => heading.href ? /*#__PURE__*/_jsx("a", {
    href: heading.href,
    className: "link-unstyled"
  }, "sectionHeading", heading.label) : heading;
  const headingAggregateClassName = clsx({
    'margin-bottom-2': ['level-1', 'level-2', 'level-3', 'section-1', 'section-2', 'section-3'].includes(uiContext)
  }, alignmentClassName, headingClassName);
  const sectionClassName = clsx(className, alignmentClassName);
  const getProps = function (sizes, additionalProps) {
    if (additionalProps === void 0) {
      additionalProps = {};
    }
    const sizeValue = sizes[uiContext];
    return [1, 2, 3].includes(sizeValue) ? {
      ...additionalProps,
      level: sizeValue
    } : {
      ...additionalProps,
      size: sizeValue
    };
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "section",
    className: sectionClassName
  }, rest), heading && /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: headingAggregateClassName,
    componentClass: headingComponentClass
  }, getProps(headingSizes, headingProps)), !!headingIcon?.type && getIcon(headingIcon), getHeading()), subheading && /*#__PURE__*/React.createElement(SubHeading, Object.assign({
    className: alignmentClassName
  }, getProps(subheadingSizes)), subheading), children && /*#__PURE__*/_jsx("div", {
    className: clsx(alignmentClassName, sectionBodyClassName)
  }, void 0, children));
}
export default Section;